import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FunnelFooter } from '../-funnel-components/funnel-footer';
import { FunnelToolbar } from '../-funnel-components/funnel-toolbar';
import { OutlineEditor } from '../-funnel-components/outline-editor';
import { Skeleton } from '../../../../components/skeleton';
import { SwitchField } from '../../../../components/switch';
import { ToastVariant } from '../../../../components/toast';
import { ContentAiFunnelStatus, type ContentOutline } from '../../../../entity/api/content-funnel';
import { ForbiddenError } from '../../../../error';
import { convertError } from '../../../../error/convert';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { type ErrorAny } from '../../../../error/utils';
import { captureException } from '../../../../logger/capture-exception';
import { getQueryClient } from '../../../../sdk/request';
import { PostHogEvents, capturePostHogEvent } from '../../../../services/posthog';
import {
  fetchArticleByIdImperative,
  useArticleFunnelByArticleId,
  useSaveArticleFunnel,
} from '../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../services/translation';
import { useToast } from '../../../../shared/contexts/toast-context';
import { toOutlineCompoundHTML } from '../../../../shared/tiptap/custom-extensions/outline-compound-heading';

const t = tNamespace('app.funnel-outlines');
const tErrors = tNamespace('errors');

export const Route = createFileRoute('/app/funnel/$id/_layout-funnel/outlines')({
  beforeLoad: async function (data: { params: { id: string } }) {
    addTrackCrumb({
      category: 'route',
      message: '/app/funnel/$id/_layout-funnel/outlines',
      level: 'info',
    });
    addTrackCrumb({
      category: 'funnel',
      message: 'entering funnel outlines',
      level: 'info',
    });

    const { id } = data.params;
    const queryClient = getQueryClient();

    try {
      await fetchArticleByIdImperative(queryClient, id);
    } catch (err) {
      captureException(
        err,
        {
          file: 'funnel/$id/outlines',
          method: 'loader',
          infoMessage: 'unable to get article by id',
          id,
        },
        trackError
      );

      throw err;
    }
  },
  // eslint-disable-next-line no-use-before-define
  component: OutlinesRoute,
});

function generateOutlineHTML(selectedOutline: ContentOutline, selectedTitle: string) {
  let html = '';
  html += toOutlineCompoundHTML({
    title: selectedTitle,
    tag: 'h1',
    instructions: [],
  });
  if (selectedOutline != null && selectedOutline.length > 0) {
    for (let i = 0; i < selectedOutline.length; i++) {
      if (i === 0) {
        // we skip the first heading as we are already adding it above
        continue;
      }
      const outline = selectedOutline[i];
      html += toOutlineCompoundHTML({
        title: outline.text,
        tag: outline.tag as 'h1' | 'h2' | 'h3',
        instructions: outline.instructions, // instructions are supposed to be empty at this point
        key: i,
      });
    }
  }
  return html;
}

function OutlinesRoute() {
  const { id } = Route.useParams() as unknown as { id: string };

  const navigate = useNavigate();
  const addToast = useToast();

  // TanStack Query hooks
  const articleFunnelQuery = useArticleFunnelByArticleId(id);
  const articleFunnel = articleFunnelQuery.data ?? null;
  const saveFunnelMutation = useSaveArticleFunnel();

  const [showInstructions, setShowInstructions] = useState(true);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const htmlVersionsRef = useRef<ContentOutline>([]);
  const lastSavedOutlines = useRef<ContentOutline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const minNumberOfOutlines = 3;

  // Add a flag to prevent stores from overriding our manual changes
  const isManuallyChanging = useRef(false);

  const outlines = useMemo(
    function () {
      // Skip updates if we're manually changing outlines
      if (isManuallyChanging.current) {
        return [];
      }

      if (articleFunnel == null) {
        return [];
      }

      const { selectedTitle, selectedOutline, aiOutlines } = articleFunnel;
      if (selectedTitle == '') {
        return [];
      }

      const outlineToUse =
        selectedOutline != null && selectedOutline.length > 0 ? selectedOutline : aiOutlines;

      const prepopulatedContent = generateOutlineHTML(outlineToUse, selectedTitle);

      setHtmlContent(prepopulatedContent);
      htmlVersionsRef.current = outlineToUse;
      // Initialize lastSavedOutlines on first load
      if (lastSavedOutlines.current == null) {
        lastSavedOutlines.current = outlineToUse;
      }
      return outlineToUse;
    },
    [articleFunnel, isManuallyChanging]
  );

  const saveOutlines = useCallback(
    function (newOutlines: ContentOutline) {
      // Skip if we're manually changing outlines
      if (isManuallyChanging.current || newOutlines.length === 0) {
        return;
      }

      if (JSON.stringify(lastSavedOutlines.current) === JSON.stringify(newOutlines)) {
        return;
      }

      lastSavedOutlines.current = newOutlines;

      saveFunnelMutation.mutate(
        {
          articleId: id,
          startNextStepGeneration: false,
          data: {
            selectedOutline: newOutlines,
          },
        },
        {
          onError: function (err) {
            captureException(
              err,
              {
                file: 'funnel/$id/outlines',
                method: 'saveOutlines',
                infoMessage: 'unable to save outlines',
                id,
              },
              trackError
            );
          },
        }
      );
    },
    [id, saveFunnelMutation]
  );

  async function generateContent() {
    if (outlines == null || outlines.length < minNumberOfOutlines) {
      setError(t('outlinesTooShort'));
      return;
    }

    const startNextStepGeneration = articleFunnel?.status === ContentAiFunnelStatus.ReqGenOutlines;

    addTrackCrumb({
      category: 'funnel',
      message: `advancing content funnel to ${ContentAiFunnelStatus.ReqGenContent}`,
      level: 'info',
    });

    try {
      await saveFunnelMutation.mutateAsync({
        articleId: id,
        startNextStepGeneration,
        data: {
          selectedOutline: outlines,
        },
      });

      capturePostHogEvent(PostHogEvents.FUNNEL_CONTENT_GENERATING, { contentId: id });
      await navigate({
        to: '/app/blogs',
      });
    } catch (err) {
      const handledErr = convertError(err);

      // handle forbiddens (which comes from plan features limitations)
      // as such, the original message is a specific enum incoming
      if (handledErr instanceof ForbiddenError) {
        addToast({
          variant: ToastVariant.Error,
          title: tErrors(handledErr.originalMessage),
          id: Date.now(),
        });
        return;
      }

      addToast({
        title: tGlobal('somethingWentWrong'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      captureException(
        handledErr,
        {
          file: 'funnel/$id/outlines',
          method: 'onSubmit',
          infoMessage: 'unable to save outlines or navigate',
          id,
        },
        trackError
      );
    }
  }

  function removeError() {
    setError(null);
  }

  useEffect(
    function () {
      if (
        articleFunnel?.status === ContentAiFunnelStatus.Brief ||
        articleFunnel?.status === ContentAiFunnelStatus.ReqGenTitles
      ) {
        navigate({
          to: `/app/blogs`,
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'funnel/$id/outlines',
              method: 'useEffect',
              infoMessage: 'unable to navigate to /app/funnel/${id}/title',
              id,
            },
            trackError
          );
        });
      }
    },
    [articleFunnel, id, navigate]
  );

  if (
    articleFunnel == null ||
    articleFunnel.status === ContentAiFunnelStatus.ReqGenOutlinesLoad ||
    htmlContent.length === 0
  ) {
    return (
      <div className="relative flex-1 overflow-hidden pb-14">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <FunnelToolbar contentFunnel={articleFunnel} isLoading={saveFunnelMutation.isPending} />
        <div className="relative h-full flex-1 overflow-auto">
          <div className="flex justify-center px-10">
            <div className="flex w-full max-w-content-editor flex-col">
              <div className="mb-4 mt-6 flex items-center justify-between">
                <p className="font-display-3">{t('outlinesTitle')}</p>
                <div className="flex items-center gap-4">
                  <SwitchField
                    size="sm"
                    label={t('seeInstructions')}
                    checked={showInstructions}
                    onCheckedChange={function () {
                      setShowInstructions(!showInstructions);
                    }}
                  />
                </div>
              </div>
              <p className="font-body-2-sb">{t('outlinesSubTitle')}</p>
              <p className="font-body-2-r mb-10 text-branding-gray">
                {t('outlinesSubTitleDescription')}
              </p>

              <OutlineEditor
                outlineLength={outlines?.length ?? 0}
                content={htmlContent}
                minNumberOfOutlines={minNumberOfOutlines}
                onUpdate={saveOutlines}
                showInstructions={showInstructions}
                removeError={removeError}
              />
              {error != null ? <p className="text-red-500">{error}</p> : null}
            </div>
          </div>
        </div>
        <FunnelFooter
          contentFunnel={articleFunnel}
          isLoading={saveFunnelMutation.isPending}
          onSubmit={function () {
            void generateContent();
          }}
          onPrevious={function () {
            navigate({
              to: `/app/funnel/${id}/title`,
              params: { id },
            }).catch(function (err: ErrorAny) {
              captureException(
                err,
                {
                  file: 'funnel/$id/outlines',
                  method: 'onPrevious',
                  infoMessage: 'unable to navigate to /app/funnel/${id}/title',
                  id,
                },
                trackError
              );
            });
          }}
        />
      </div>
    </>
  );
}
